import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, Sparkles, AlertCircle, ExternalLink } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";

function OpenFdpNotice({ open }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200" data-testid="fdp-school-has-open">
      <Handshake className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-violet-900">Escola com FDP ativo</p>
        <p className="text-xs text-violet-800/80 mt-0.5">Já existe um Faz Depois Paga em aberto (um por vez).</p>
        <Link to={`/orcamentos/${open.id}`} className="inline-flex items-center gap-1 mt-1.5 text-xs font-bold text-[#7c3aed] hover:underline" data-testid="link-open-fdp">
          <ExternalLink className="w-3 h-3" /> Abrir FDP Nº {open.number}
        </Link>
      </div>
    </div>
  );
}

function LogModeToggle({ checked, onChange }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-indigo-50 to-sky-50 border-2 border-indigo-200" data-testid="fdp-log-mode-toggle">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid="check-fdp-log-mode" />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> Modalidade: Lançamentos ao longo do período
        </p>
        <p className="text-[11px] text-slate-600 leading-relaxed">
          Orçamento começa vazio (não precisa de item). Você registra cada serviço como <b>lançamento</b> na tela de detalhes; no fim, a IA consolida em itens. Desmarcado = FDP com itens normais.
        </p>
      </div>
    </div>
  );
}

export default function BudgetFdpOptions({ form, update, school, budgetId, onActivate }) {
  const [openFdp, setOpenFdp] = useState(null);
  const isNew = !budgetId;

  useEffect(() => {
    setOpenFdp(null);
    if (form.school_id) {
      api.get(`/fdp/schools/${form.school_id}/open-budget`).then((r) => setOpenFdp(r.data || null)).catch(() => {});
    }
  }, [form.school_id]);

  const blockedByOther = !!openFdp && openFdp.id !== budgetId && !(budgetId && form.faz_depois_paga);
  const hasFdp = !!school?.fdp_active;

  useEffect(() => {
    if (form.faz_depois_paga && form.school_id && school && (!hasFdp || (isNew && blockedByOther))) {
      update("faz_depois_paga", false);
      update("fdp_log_mode", false);
    }
  }, [form.faz_depois_paga, form.school_id, school, hasFdp, blockedByOther, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form.school_id) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="fdp-locked-no-school">
        <AlertCircle className="w-5 h-5 text-slate-400 shrink-0" />
        <p className="text-xs text-slate-500">Selecione uma escola primeiro para habilitar a opção "Faz Depois Paga".</p>
      </div>
    );
  }
  if (blockedByOther) return <OpenFdpNotice open={openFdp} />;
  if (!hasFdp) {
    return (
      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="fdp-locked-not-active">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">Esta escola ainda não está ativa no sistema Faz Depois Paga</p>
          <p className="text-xs text-amber-800 mt-0.5">Para marcar este orçamento como FDP, primeiro ative a modalidade (limite e período).</p>
          <button type="button" onClick={onActivate} className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#7c3aed] hover:underline" data-testid="btn-open-fdp-activate">
            <Handshake className="w-3 h-3" /> Ativar FDP para esta escola
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200" data-testid="fdp-available">
        <Checkbox
          checked={form.faz_depois_paga}
          onCheckedChange={(v) => { update("faz_depois_paga", !!v); if (!v) update("fdp_log_mode", false); }}
          data-testid="check-fdp"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">Este orçamento será do sistema Faz Depois Paga</p>
          <p className="text-xs text-slate-500">
            Escola ativa na modalidade — limite {BRL(school.fdp_max_value || 0)} / {school.fdp_period_months || 0} meses. Sem validade e sem prazo de entrega.
          </p>
        </div>
        <Handshake className="w-5 h-5 text-violet-600 shrink-0" />
      </div>
      {form.faz_depois_paga && isNew && <LogModeToggle checked={form.fdp_log_mode} onChange={(v) => update("fdp_log_mode", v)} />}
      {form.faz_depois_paga && !isNew && form.fdp_log_mode && (
        <div className="flex items-center gap-2 p-2 rounded bg-indigo-100 border border-indigo-200">
          <Sparkles className="w-3.5 h-3.5 text-indigo-700 shrink-0" />
          <p className="text-[11px] text-indigo-900"><b>Modo lançamentos ativo:</b> use o painel "Lançamentos FDP" na tela de detalhes para registrar serviços.</p>
        </div>
      )}
    </div>
  );
}
